import { Activity, Edit3, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import {
  readingValueAsNumber,
} from '@/features/counter-readings/format';
import {
  useEquipmentCounterReadings,
  useLatestEquipmentReadings,
} from '@/features/counter-readings/queries';
import type { CounterReading } from '@/features/counter-readings/types';
import type {
  CounterFieldConfig,
  EquipmentType,
} from '@/features/equipment-types/types';
import type { Equipment } from '@/features/equipment/types';
import { MiniLineChart } from './MiniLineChart';
import { RecordCounterModal } from './RecordCounterModal';
import { SupersedeReadingModal } from './SupersedeReadingModal';

interface CountersCardProps {
  equipment: Equipment;
}

export function CountersCard({ equipment }: CountersCardProps) {
  const { t } = useTranslation();
  const { data: types } = useEquipmentTypes();
  const equipmentType: EquipmentType | undefined = useMemo(
    () => types?.find((x) => x.id === equipment.equipmentTypeId),
    [types, equipment.equipmentTypeId],
  );
  const fields = equipmentType?.counterFields ?? [];
  const { data: latestMap = {} } = useLatestEquipmentReadings(equipment.id);

  // No counter declared on the type → render nothing (don't pollute
  // the detail view with empty cards).
  if (fields.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity aria-hidden className="h-4 w-4" />
          {t('counter.card.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((cfg) => (
          <CounterRow
            key={cfg.field}
            equipment={equipment}
            field={cfg}
            latest={latestMap[cfg.field] ?? null}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function CounterRow({
  equipment,
  field,
  latest,
}: {
  equipment: Equipment;
  field: CounterFieldConfig;
  latest: CounterReading | null;
}) {
  const { t } = useTranslation();
  const { data: readings = [], isLoading } = useEquipmentCounterReadings(
    equipment.id,
    { field: field.field, limit: 20 },
  );
  const [recordOpen, setRecordOpen] = useState(false);
  const [supersedeOpen, setSupersedeOpen] = useState(false);

  const fieldLabel =
    t(`counter.field.${field.field}`, { defaultValue: field.label });

  const points = readings
    .slice() // immutable sort
    .sort((a, b) => (a.readAt < b.readAt ? -1 : 1))
    .map((r) => ({
      x: new Date(r.readAt).getTime(),
      y: readingValueAsNumber(r),
    }));

  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{fieldLabel}</p>
          {latest ? (
            <p className="text-xs text-muted-foreground">
              {t('counter.card.lastReading', {
                value: Number(latest.value).toFixed(2),
                unit: field.unit,
                date: new Date(latest.readAt).toLocaleDateString(),
              })}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t('counter.card.lastReadingEmpty')}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setRecordOpen(true)}>
            <Plus aria-hidden className="h-3.5 w-3.5" />
            {t('counter.card.addAction')}
          </Button>
          {latest && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSupersedeOpen(true)}
              aria-label={t('counter.card.supersedeAction')}
            >
              <Edit3 aria-hidden className="h-3.5 w-3.5" />
              {t('counter.card.supersedeAction')}
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : points.length >= 2 ? (
        <MiniLineChart points={points} unit={field.unit} />
      ) : null}

      <RecordCounterModal
        open={recordOpen}
        onOpenChange={setRecordOpen}
        equipmentId={equipment.id}
        field={field}
        lastReading={latest}
      />

      {latest && (
        <SupersedeReadingModal
          open={supersedeOpen}
          onOpenChange={setSupersedeOpen}
          reading={latest}
        />
      )}
    </div>
  );
}
